import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  FolderIcon,
  ClockIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { useProjectStore } from '../store/projectStore';
import { useClientStore } from '../store/clientStore';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import {
  Project,
  Category,
  CategoryCreate,
  ProjectSubproject,
  categoriesAPI,
  subprojectsAPI,
} from '../services/api';

// Predefined color palette for categories
const COLOR_PALETTE = [
  '#6B7280', '#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16',
  '#22C55E', '#10B981', '#14B8A6', '#06B6D4', '#0EA5E9', '#3B82F6',
  '#6366F1', '#8B5CF6', '#A855F7', '#D946EF', '#EC4899', '#F43F5E',
];

type ModalTab = 'details' | 'categories' | 'subprojects';

export const ProjectsPage: React.FC = () => {
  const { t } = useTranslation();
  const { projects, isLoading, error, fetchProjects, createProject, updateProject, deleteProject, clearError } = useProjectStore();
  const { clients, fetchClients } = useClientStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [deletingProject, setDeletingProject] = useState<Project | null>(null);
  const [formData, setFormData] = useState({
    name: '', description: '', client_id: '',
    reporting_period_type: '',
    reporting_period_config: {} as Record<string, any>,
  });
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filterClientId, setFilterClientId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<ModalTab>('details');

  // Categories state (project-scoped)
  const [projectCategories, setProjectCategories] = useState<Category[]>([]);
  const [isCatAddOpen, setIsCatAddOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [catFormData, setCatFormData] = useState<CategoryCreate>({ name: '', color: '#6B7280' });
  const [catFormError, setCatFormError] = useState('');
  const [isCatSubmitting, setIsCatSubmitting] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null);

  // Subprojects state (project-scoped)
  const [projectSubprojects, setProjectSubprojects] = useState<ProjectSubproject[]>([]);
  const [isSubAddOpen, setIsSubAddOpen] = useState(false);
  const [editingSubproject, setEditingSubproject] = useState<ProjectSubproject | null>(null);
  const [subFormName, setSubFormName] = useState('');
  const [subFormError, setSubFormError] = useState('');
  const [isSubSubmitting, setIsSubSubmitting] = useState(false);
  const [deletingSubproject, setDeletingSubproject] = useState<ProjectSubproject | null>(null);

  useEffect(() => {
    fetchProjects();
    fetchClients();
  }, [fetchProjects, fetchClients]);

  const filteredProjects = filterClientId
    ? projects.filter(p => p.client_id === filterClientId)
    : projects;

  // Fetch categories and subprojects when editing a project
  const fetchProjectConfig = async (projectId: string) => {
    try {
      const [cats, subs] = await Promise.all([
        categoriesAPI.list(projectId),
        subprojectsAPI.list(projectId),
      ]);
      setProjectCategories(cats);
      setProjectSubprojects(subs);
    } catch (err) {
      console.error('Failed to fetch project config:', err);
    }
  };

  const handleOpenModal = (project?: Project) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        name: project.name,
        description: project.description || '',
        client_id: project.client_id,
        reporting_period_type: project.reporting_period_type || '',
        reporting_period_config: project.reporting_period_config || {},
      });
      setActiveTab('details');
      fetchProjectConfig(project.id);
    } else {
      setEditingProject(null);
      setFormData({ name: '', description: '', client_id: clients[0]?.id || '', reporting_period_type: '', reporting_period_config: {} });
      setActiveTab('details');
      setProjectCategories([]);
      setProjectSubprojects([]);
    }
    setFormError('');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (isCatAddOpen || deletingCategory || isSubAddOpen || deletingSubproject) return;
    setIsModalOpen(false);
    setEditingProject(null);
    setFormData({ name: '', description: '', client_id: '', reporting_period_type: '', reporting_period_config: {} });
    setFormError('');
    setActiveTab('details');
    setProjectCategories([]);
    setProjectSubprojects([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setFormError(t('errors.required'));
      return;
    }
    if (!formData.client_id) {
      setFormError('Please select a client');
      return;
    }

    setIsSubmitting(true);
    try {
      const periodType = formData.reporting_period_type || undefined;
      const periodConfig = formData.reporting_period_type ? formData.reporting_period_config : undefined;

      if (editingProject) {
        await updateProject(editingProject.id, {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          client_id: formData.client_id,
          reporting_period_type: periodType,
          reporting_period_config: periodConfig,
        });
      } else {
        const created = await createProject({
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          client_id: formData.client_id,
          reporting_period_type: periodType,
          reporting_period_config: periodConfig,
        });
        // Transition to edit mode to show categories/subprojects tabs
        setEditingProject(created);
        fetchProjectConfig(created.id);
      }
      setFormError('');
      if (editingProject) {
        handleCloseModal();
      }
    } catch (err: any) {
      setFormError(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (project: Project) => {
    setDeletingProject(project);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingProject) return;

    setIsSubmitting(true);
    try {
      await deleteProject(deletingProject.id);
      setIsDeleteModalOpen(false);
      setDeletingProject(null);
    } catch (err) {
      // Error is handled by the store
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Category CRUD ────────────────────────────────────────────

  const handleCatOpenAdd = () => {
    setEditingCategory(null);
    setCatFormData({ name: '', color: '#6B7280' });
    setCatFormError('');
    setIsCatAddOpen(true);
  };

  const handleCatOpenEdit = (cat: Category) => {
    setEditingCategory(cat);
    setCatFormData({ name: cat.name, color: cat.color });
    setCatFormError('');
    setIsCatAddOpen(true);
  };

  const handleCatCloseAdd = () => {
    setIsCatAddOpen(false);
    setEditingCategory(null);
    setCatFormData({ name: '', color: '#6B7280' });
    setCatFormError('');
  };

  const handleCatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!catFormData.name.trim()) {
      setCatFormError('Category name is required');
      return;
    }
    if (!editingProject) return;

    setIsCatSubmitting(true);
    try {
      if (editingCategory) {
        await categoriesAPI.update(editingProject.id, editingCategory.id, {
          name: catFormData.name.trim(),
          color: catFormData.color,
        });
      } else {
        await categoriesAPI.create(editingProject.id, {
          name: catFormData.name.trim(),
          color: catFormData.color,
        });
      }
      const cats = await categoriesAPI.list(editingProject.id);
      setProjectCategories(cats);
      handleCatCloseAdd();
    } catch (err: any) {
      setCatFormError(err.response?.data?.detail || 'Failed to save category');
    } finally {
      setIsCatSubmitting(false);
    }
  };

  const handleCatDelete = async () => {
    if (!deletingCategory || !editingProject) return;

    setIsCatSubmitting(true);
    try {
      await categoriesAPI.delete(editingProject.id, deletingCategory.id);
      const cats = await categoriesAPI.list(editingProject.id);
      setProjectCategories(cats);
      setDeletingCategory(null);
    } catch (err: any) {
      setCatFormError(err.response?.data?.detail || 'Failed to delete category');
    } finally {
      setIsCatSubmitting(false);
    }
  };

  // ─── Subproject CRUD ──────────────────────────────────────────

  const handleSubOpenAdd = () => {
    setEditingSubproject(null);
    setSubFormName('');
    setSubFormError('');
    setIsSubAddOpen(true);
  };

  const handleSubOpenEdit = (sub: ProjectSubproject) => {
    setEditingSubproject(sub);
    setSubFormName(sub.name);
    setSubFormError('');
    setIsSubAddOpen(true);
  };

  const handleSubCloseAdd = () => {
    setIsSubAddOpen(false);
    setEditingSubproject(null);
    setSubFormName('');
    setSubFormError('');
  };

  const handleSubSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subFormName.trim()) {
      setSubFormError('Subproject name is required');
      return;
    }
    if (!editingProject) return;

    setIsSubSubmitting(true);
    try {
      if (editingSubproject) {
        await subprojectsAPI.update(editingProject.id, editingSubproject.id, {
          name: subFormName.trim(),
        });
      } else {
        await subprojectsAPI.create(editingProject.id, { name: subFormName.trim() });
      }
      const subs = await subprojectsAPI.list(editingProject.id);
      setProjectSubprojects(subs);
      handleSubCloseAdd();
    } catch (err: any) {
      setSubFormError(err.response?.data?.detail || 'Failed to save subproject');
    } finally {
      setIsSubSubmitting(false);
    }
  };

  const handleSubDelete = async () => {
    if (!deletingSubproject || !editingProject) return;

    setIsSubSubmitting(true);
    try {
      await subprojectsAPI.delete(editingProject.id, deletingSubproject.id);
      const subs = await subprojectsAPI.list(editingProject.id);
      setProjectSubprojects(subs);
      setDeletingSubproject(null);
    } catch (err: any) {
      setSubFormError(err.response?.data?.detail || 'Failed to delete subproject');
    } finally {
      setIsSubSubmitting(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────

  const activeCategories = projectCategories.filter(c => c.is_active);
  const activeSubprojects = projectSubprojects.filter(s => s.is_active);

  if (isLoading && projects.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const tabClasses = (tab: ModalTab) =>
    `px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
      activeTab === tab
        ? 'bg-white dark:bg-gray-800 text-green-600 dark:text-green-400 border-b-2 border-green-600 dark:border-green-400'
        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
    }`;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('projects.title')}
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {filteredProjects.length} {filteredProjects.length === 1 ? 'project' : 'projects'}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={filterClientId}
            onChange={(e) => setFilterClientId(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="">All Clients</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => handleOpenModal()}
            disabled={clients.length === 0}
            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            {t('projects.addProject')}
          </button>
        </div>
      </div>

      {/* No clients warning */}
      {clients.length === 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            You need to create a client before you can add projects.
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          <button onClick={clearError} className="text-sm text-red-800 dark:text-red-300 underline mt-1">
            Dismiss
          </button>
        </div>
      )}

      {/* Projects Grid */}
      {filteredProjects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                    <FolderIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {project.name}
                    </h3>
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <UserGroupIcon className="h-3.5 w-3.5 mr-1" />
                      {project.client_name}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => handleOpenModal(project)}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title={t('actions.edit')}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(project)}
                    className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title={t('actions.delete')}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {project.description && (
                <p className="mt-3 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                  {project.description}
                </p>
              )}

              <div className="mt-2 flex flex-wrap gap-1.5">
                {project.reporting_period_type && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                    {project.reporting_period_type === 'semi_monthly' ? 'Semi-monthly' :
                     project.reporting_period_type === 'monthly' ? 'Monthly' :
                     project.reporting_period_type === 'weekly' ? 'Weekly' :
                     project.reporting_period_type === 'bi_weekly' ? 'Bi-weekly' :
                     project.reporting_period_type}
                  </span>
                )}
                {!project.is_active && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                    Inactive
                  </span>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-sm">
                <div className="flex items-center text-gray-500 dark:text-gray-400">
                  <ClockIcon className="h-4 w-4 mr-1" />
                  <span>{((project as any).total_hours || 0).toFixed(1)} hrs</span>
                </div>
                <div className="text-gray-500 dark:text-gray-400">
                  {(project as any).entry_count || 0} {t('projects.entries')}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
          <FolderIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            {t('projects.noProjects')}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('projects.noProjectsDescription')}
          </p>
          {clients.length > 0 && (
            <button
              onClick={() => handleOpenModal()}
              className="mt-4 inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              {t('projects.addProject')}
            </button>
          )}
        </div>
      )}

      {/* Add/Edit Modal with Tabs */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingProject ? t('projects.editProject') : t('projects.addProject')}
        size={editingProject ? 'lg' : 'md'}
      >
        {/* Tabs (only show all tabs when editing) */}
        {editingProject && (
          <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4 -mt-2">
            <button onClick={() => setActiveTab('details')} className={tabClasses('details')}>
              {t('projects.tabs.details')}
            </button>
            <button onClick={() => setActiveTab('categories')} className={tabClasses('categories')}>
              {t('projects.tabs.categories')} ({activeCategories.length})
            </button>
            <button onClick={() => setActiveTab('subprojects')} className={tabClasses('subprojects')}>
              {t('projects.tabs.subprojects')} ({activeSubprojects.length})
            </button>
          </div>
        )}

        {/* ─── Details Tab ─── */}
        {activeTab === 'details' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('projects.selectClient')}
              </label>
              <select
                value={formData.client_id}
                onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Select a client...</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label={t('projects.projectName')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter project name"
              error={formError}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('projects.description')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Reporting Period */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('projects.reportingPeriod')}
              </label>
              <select
                value={formData.reporting_period_type}
                onChange={(e) => {
                  const type = e.target.value;
                  setFormData({ ...formData, reporting_period_type: type, reporting_period_config: {} });
                }}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">None</option>
                <option value="semi_monthly">Semi-monthly (1st-15th, 16th-end)</option>
                <option value="monthly">Monthly</option>
                <option value="weekly">Weekly</option>
                <option value="bi_weekly">Bi-weekly</option>
              </select>
            </div>

            {formData.reporting_period_type === 'weekly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('projects.weekStartDay')}
                </label>
                <select
                  value={formData.reporting_period_config.start_day || 'Monday'}
                  onChange={(e) => setFormData({
                    ...formData,
                    reporting_period_config: { ...formData.reporting_period_config, start_day: e.target.value },
                  })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>
            )}

            {formData.reporting_period_type === 'bi_weekly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('projects.anchorDate')}
                </label>
                <input
                  type="date"
                  value={formData.reporting_period_config.anchor_date || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    reporting_period_config: { ...formData.reporting_period_config, anchor_date: e.target.value },
                  })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={handleCloseModal} type="button">
                {t('actions.cancel')}
              </Button>
              <Button type="submit" isLoading={isSubmitting}>
                {t('actions.save')}
              </Button>
            </div>
          </form>
        )}

        {/* ─── Categories Tab ─── */}
        {activeTab === 'categories' && editingProject && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('projects.categories.description')}
              </p>
              <Button onClick={handleCatOpenAdd} size="sm">
                <PlusIcon className="h-4 w-4 mr-1" />
                {t('actions.add')}
              </Button>
            </div>

            <div className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              {activeCategories.length === 0 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                  {t('projects.categories.noCategories')}
                </div>
              ) : (
                activeCategories.map((category) => (
                  <div
                    key={category.id}
                    className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: category.color }}
                      />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {category.name}
                      </span>
                      {category.is_default && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {t('projects.categories.default')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleCatOpenEdit(category)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        title={t('actions.edit')}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeletingCategory(category)}
                        className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        title={t('actions.delete')}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="flex justify-end pt-2">
              <Button variant="secondary" onClick={handleCloseModal}>
                {t('actions.close')}
              </Button>
            </div>
          </div>
        )}

        {/* ─── Subprojects Tab ─── */}
        {activeTab === 'subprojects' && editingProject && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('projects.subprojects.description')}
              </p>
              <Button onClick={handleSubOpenAdd} size="sm">
                <PlusIcon className="h-4 w-4 mr-1" />
                {t('actions.add')}
              </Button>
            </div>

            <div className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              {activeSubprojects.length === 0 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                  {t('projects.subprojects.noSubprojects')}
                </div>
              ) : (
                activeSubprojects.map((subproject) => (
                  <div
                    key={subproject.id}
                    className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {subproject.name}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleSubOpenEdit(subproject)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        title={t('actions.edit')}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeletingSubproject(subproject)}
                        className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        title={t('actions.delete')}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="flex justify-end pt-2">
              <Button variant="secondary" onClick={handleCloseModal}>
                {t('actions.close')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Category Add/Edit Portal Modal */}
      {isCatAddOpen && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-25" onClick={handleCatCloseAdd} />
          <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {editingCategory ? t('projects.categories.editCategory') : t('projects.categories.addCategory')}
              </h3>
              <button
                onClick={handleCatCloseAdd}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleCatSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('projects.categories.name')}
                </label>
                <input
                  type="text"
                  autoFocus
                  value={catFormData.name}
                  onChange={(e) => setCatFormData({ ...catFormData, name: e.target.value })}
                  placeholder="e.g., Documentation"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
                />
                {catFormError && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{catFormError}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('projects.categories.color')}
                </label>
                <div className="grid grid-cols-9 gap-2">
                  {COLOR_PALETTE.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setCatFormData({ ...catFormData, color })}
                      className={`w-6 h-6 rounded-full transition-transform ${
                        catFormData.color === color
                          ? 'ring-2 ring-offset-2 ring-green-500 dark:ring-offset-gray-800 scale-110'
                          : 'hover:scale-110'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <div
                    className="w-8 h-8 rounded-full border-2 border-gray-200 dark:border-gray-600"
                    style={{ backgroundColor: catFormData.color }}
                  />
                  <input
                    type="text"
                    value={catFormData.color}
                    onChange={(e) => setCatFormData({ ...catFormData, color: e.target.value.toUpperCase() })}
                    className="flex-1 px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="#6B7280"
                    pattern="^#[0-9A-Fa-f]{6}$"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="secondary" onClick={handleCatCloseAdd} type="button">
                  {t('actions.cancel')}
                </Button>
                <Button type="submit" isLoading={isCatSubmitting}>
                  {editingCategory ? t('actions.save') : t('actions.create')}
                </Button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Category Delete Confirmation Portal */}
      {deletingCategory && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setDeletingCategory(null)} />
          <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              {t('projects.categories.deleteCategory')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {t('projects.categories.deleteConfirm', { name: deletingCategory.name })}
            </p>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="secondary" onClick={() => setDeletingCategory(null)}>
                {t('actions.cancel')}
              </Button>
              <Button variant="danger" onClick={handleCatDelete} isLoading={isCatSubmitting}>
                {t('actions.delete')}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Subproject Add/Edit Portal Modal */}
      {isSubAddOpen && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-25" onClick={handleSubCloseAdd} />
          <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {editingSubproject ? t('projects.subprojects.editSubproject') : t('projects.subprojects.addSubproject')}
              </h3>
              <button
                onClick={handleSubCloseAdd}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('projects.subprojects.name')}
                </label>
                <input
                  type="text"
                  autoFocus
                  value={subFormName}
                  onChange={(e) => setSubFormName(e.target.value)}
                  placeholder="e.g., Frontend, Backend, API"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
                />
                {subFormError && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{subFormError}</p>}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="secondary" onClick={handleSubCloseAdd} type="button">
                  {t('actions.cancel')}
                </Button>
                <Button type="submit" isLoading={isSubSubmitting}>
                  {editingSubproject ? t('actions.save') : t('actions.create')}
                </Button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Subproject Delete Confirmation Portal */}
      {deletingSubproject && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setDeletingSubproject(null)} />
          <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              {t('projects.subprojects.deleteSubproject')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {t('projects.subprojects.deleteConfirm', { name: deletingSubproject.name })}
            </p>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="secondary" onClick={() => setDeletingSubproject(null)}>
                {t('actions.cancel')}
              </Button>
              <Button variant="danger" onClick={handleSubDelete} isLoading={isSubSubmitting}>
                {t('actions.delete')}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Project Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title={t('actions.delete')}
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            {t('projects.deleteConfirm')}
          </p>
          {deletingProject && (
            <p className="font-medium text-gray-900 dark:text-white">
              "{deletingProject.name}"
            </p>
          )}
          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
              {t('actions.cancel')}
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete} isLoading={isSubmitting}>
              {t('actions.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
